const PAGE_NAMES_WITH_PUBLICATION_STATE = new Set([
  'model_core_feature_specs',
  'model_core_review',
  'model_core',
  'model_car_inventory_vin_detail',
]);

const PAGE_CATEGORIES_WITH_PUBLICATION_STATE = new Set(['model_core', 'model_car_inventory']);

const CATEGORY_PAGE_PATHS = new Set([
  '/new-cars/',
  '/used-cars-for-sale/',
  '/used-all/',
  '/new-cars-for-sale/',
  '/electric-car/',
  '/suv/',
  '/truck/',
  '/sedan/',
  '/hybrid/',
  '/ford/',
  '/acura/',
  '/bmw/',
  '/chevrolet/',
  '/dodge/',
  '/honda/',
  '/infiniti/',
  '/kia/',
  '/mazda/',
  '/toyota/',
  '/audi/',
  '/buick/',
  '/chrysler/',
  '/fiat/',
  '/gmc/',
  '/hyundai/',
  '/jeep/',
  '/lexus/',
  '/subaru/',
  '/volkswagen/',
]);

const CATEGORY2_PAGE_PATHS = new Set(['/electric-car/', '/suv/', '/truck/', '/sedan/', '/hybrid/']);

const NESTED_DATA_LAYER_KEYS = new Set(['event_data', 'lead_data']);

const STATIC_EVENT_PARAMETERS = {
  synpartner: 'find',
  schema_version: 'e17_1.0',
  event_data: 'needed',
};

const STATIC_EVENT_PARAMETER_KEYS = new Set(Object.keys(STATIC_EVENT_PARAMETERS));

const payloadState = { ...STATIC_EVENT_PARAMETERS };

export const DATA_LAYER_PATH_BY_PAYLOAD_KEY = {
  model_link_code: 'model_link_code',
  page_name: 'page_name',
  page_category: 'page_category',
  action_cause: 'event_data.action_cause',
  action_name: 'event_data.action_name',
  creative_id: 'event_data.creative_id',
  loc_id_list: 'event_data.loc_id_list',
  f_id_list: 'event_data.f_id_list',
  inv_id_list: 'event_data.inv_id_list',
  vin_list: 'event_data.vin_list',
  match_type_list: 'event_data.match_type_list',
  cpo_results: 'event_data.cpo_results',
  new_results: 'event_data.new_results',
  used_results: 'event_data.used_results',
  match_total: 'event_data.match_total',
  match_results: 'event_data.match_results',
  cpo: 'event_data.cpo',
  inv_treatment_list: 'event_data.inv_treatment_list',
  sort_algo_yes_no: 'event_data.sort_algo_yes_no',
  sort_algo_name: 'event_data.sort_algo_name',
  price: 'event_data.price',
  price_drop: 'event_data.price_drop',
  photo_type: 'event_data.photo_type',
  days_listed: 'event_data.days_listed',
  below_market: 'event_data.below_market',
  deal_indicator: 'event_data.deal_indicator',
  five_star_dealer: 'event_data.five_star_dealer',
  vehicle_history: 'event_data.vehicle_history',
  recommended_offer: 'event_data.recommended_offer',
  dealer_rating: 'event_data.dealer_rating',
  miles_away: 'event_data.miles_away',
  radius: 'event_data.radius',
  ts: 'ts',
  campaign: 'event_data.campaign',
  recipe: 'event_data.recipe',
  request_url: 'request_url',
  referral_url: 'referral_url',
  ua: 'ua',
  title: 'title',
  screen_resolution: 'screen_resolution',
  browser_width: 'browser_width',
  geoloc_enabled: 'geoloc_enabled',
  page_uuid: 'page_uuid',
  publication_state: 'publication_state',
  make_id: 'make_id',
  model_year_id: 'model_year_id',
  submodel_id: 'submodel_id',
  type: 'type',
  style_id: 'style_id',
  visitor_id: 'visitor_id',
  ed_session_id: 'session_id',
  user_zip_code: 'user_zip_code',
  ip_zip_code: 'ip_zip_code',
  timestamp: 'timestamp',
  event_type: 'event_type',
  web_platform: 'web_platform',
  partner_id: 'partner_id',
  platform_type: 'platform_type',
  current_zip_code: 'current_zip_code',
  vin: 'vin',
  url_pattern: 'url_pattern',
  product_partner_id: 'product_partner_id',
  us_privacy: 'usprivacy',
  subaction_name: 'event_data.subaction_name',
  value: 'event_data.value',
  make_nicename: 'make_nicename',
  model_nicename: 'model_nicename',
  year: 'year',
  page_title: 'title',
  sale_price: 'sale_price',
  lead_data: 'lead_data',
  action_category: 'event_data.action_category',
  is_first_page_of_session: 'is_first_page_of_session',
  purchase_price: 'price',
  price_lead_data: 'lead_data.price',
  vin_lead_data: 'lead_data.vin',
  should_not_sell_via_cipa_policy: 'should_not_sell_via_cipa_policy',
};

const PAYLOAD_KEY_BY_DATA_LAYER_PATH = Object.entries(DATA_LAYER_PATH_BY_PAYLOAD_KEY).reduce(
  (payloadKeyByDataLayerPath, [payloadKey, dataLayerPath]) => {
    if (dataLayerPath === 'title') {
      return {
        ...payloadKeyByDataLayerPath,
        [dataLayerPath]: [...(payloadKeyByDataLayerPath[dataLayerPath] || []), payloadKey],
      };
    }

    return {
      ...payloadKeyByDataLayerPath,
      [dataLayerPath]: payloadKey,
    };
  },
  {}
);

function updatePayloadStateValue(dataLayerPath, value) {
  const payloadKey = PAYLOAD_KEY_BY_DATA_LAYER_PATH[dataLayerPath];

  if (!payloadKey || value === undefined || value === null || value === '') return;

  if (dataLayerPath === 'title') {
    payloadKey.forEach(key => {
      payloadState[key] = value;
    });
    return;
  }

  payloadState[payloadKey] = value;
}

/**
 * Extracts and updates payloadState with payload keys present in pushObject.
 */
export function updatePayloadState(pushObject) {
  if (!pushObject || typeof pushObject !== 'object') return;

  Object.entries(pushObject).forEach(([key, value]) => {
    if (!STATIC_EVENT_PARAMETER_KEYS.has(key)) {
      updatePayloadStateValue(key, value);
    }

    if (NESTED_DATA_LAYER_KEYS.has(key) && value && typeof value === 'object') {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        updatePayloadStateValue(`${key}.${nestedKey}`, nestedValue);
      });
    }
  });
}

export function getPayloadStateSnapshot() {
  return { ...payloadState };
}

function getMakeModel(state) {
  return `${state.make_nicename}-${state.model_nicename}`;
}

function getPageName(state) {
  const pageName = state.page_name;

  if (PAGE_NAMES_WITH_PUBLICATION_STATE.has(pageName)) {
    return `${state.publication_state}_${pageName}`;
  }

  return pageName;
}

function getPageCategory(state) {
  const pageCategory = state.page_category;

  if (PAGE_CATEGORIES_WITH_PUBLICATION_STATE.has(pageCategory)) {
    return `${state.publication_state}_${pageCategory}`;
  }

  return pageCategory;
}

function getUrlSnippetParts() {
  const navigationUrl = performance.getEntries?.()?.[0]?.name;

  if (!navigationUrl) return undefined;

  const match = decodeURIComponent(navigationUrl).match(/#:~:text=(.*)/);

  if (!match) return undefined;

  return match[1].replace(/,/g, '*').replace(/\//g, '').replace(/\*/g, ' ').split(' ');
}

function getUrlSnippetStart() {
  return getUrlSnippetParts()?.[0];
}

function getUrlSnippetEnd() {
  const snippetParts = getUrlSnippetParts();
  return snippetParts ? snippetParts.slice(1).join(' ') : undefined;
}

function getRtbEvent(state) {
  const pageName = getPageName(state);

  if (pageName === 'home_page') return 'home';
  if (pageName === 'new_model_core' || pageName === 'new_model_highlights') return `offer_${getMakeModel(state)}`;
  if (pageName === 'used_model_mydp_tmv_appraiser_style') {
    const makeModel = getMakeModel(state);
    return `basketstatus_${makeModel},${makeModel},${makeModel},${makeModel},${makeModel}`;
  }

  return undefined;
}

function getRtbDeduplication() {
  return new URLSearchParams(window.location.search).get('utm_account') !== 'rtbhouse';
}

function getDwellTime() {
  try {
    return sessionStorage.getItem('dTime');
  } catch {
    return undefined;
  }
}

export function getPayloadStateValue(name, state) {
  return state[name];
}

export const DERIVED_EVENT_PARAMETERS = {
  page_name: getPageName,
  page_category: getPageCategory,
  page_path: () => window.location.pathname,
  page_url: () => window.location.href,
  url_snippet_start: getUrlSnippetStart,
  url_snippet_end: getUrlSnippetEnd,
  rtb_event: getRtbEvent,
  category_page_flag: () => CATEGORY_PAGE_PATHS.has(window.location.pathname),
  rtb_deduplication: getRtbDeduplication,
  JS_dTime_Dwell: getDwellTime,
  category2_page_flag: () => CATEGORY2_PAGE_PATHS.has(window.location.pathname),
  page_category_path: () => window.location.pathname.replace(/\//g, ''),
};
